import { OpenInFull } from "@clockwise/icons";
import { ConferencingType } from "@clockwise/schema";
import { useUpdateActiveEvent } from "@clockwise/web-commons/src/util/ActiveEventContext";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { generateRescheduleCQL } from "@clockwise/web-commons/src/util/generateCQL";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { EditOutlined } from "@material-ui/icons";
import { truncate } from "lodash";
import { DateTime } from "luxon";
import React from "react";
import { useDispatch } from "react-redux";
import { ResponseStatusEnum } from "../../../__schema__";
import { addSelectedRescheduleOption } from "../../../state/actions/reschedule-event-modal.actions";
import { useAIMessageContext } from "../../chat/ai-chat/hooks/AIMessageContext";
import { useProcessMessage } from "../../chat/ai-chat/hooks/useProcessMessage";
import { useScrollChatToBottom } from "../../chat/ai-chat/hooks/useScrollChatToBottom";
import { PopperWrapper } from "../calendar-popover/PopperWrapper";
import { ListItemButton } from "../calendar-popover/atoms/ListItemButton";
import { EventPermissions } from "../types";
import { FlexQuickAction } from "./FlexQuickAction";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { JoinVideoLink } from "./JoinVideoLink";
import { QuickReschedule } from "./QuickReschedule";
import { useEventPopoverDetails } from "./hooks/useEventPopoverDetails";

export type VideoLink = {
  __typename: "VideoLink";
  uri: string;
  type: ConferencingType;
};
export const EventContextMenu = ({
  externalEventId,
  title,
  anchorEl,
  onClose,
  responseState,
  onOpenCard,
  startTime,
  calendarId,
  videoLink,
  eventPermissions: { canModify, canInviteOthers, canRSVP, canDelete, canRemove },
  side = "right",
  duration,
}: {
  externalEventId: string;
  title: string;
  anchorEl: HTMLDivElement | null;
  onClose: () => void;
  responseState: ResponseStatusEnum | "Proposed";
  onOpenCard: () => void;
  startTime: DateTime;
  calendarId: string;
  videoLink: VideoLink | null;
  eventPermissions: EventPermissions;
  side: "left" | "right";
  duration: number;
}) => {
  const track = useTracking();
  const scrollChatToBottom = useScrollChatToBottom();
  const { processMessage } = useProcessMessage();
  const updateActiveEvent = useUpdateActiveEvent();
  const { shouldExcludeAi } = useAIMessageContext();

  const dispatch = useDispatch();

  const {
    isFlexible,
    canMarkFlexible,
    loading: loadingDetails,
    refetch,
    description,
    attendees,
    currentUserId,
    ownerDisplayName,
    id,
  } = useEventPopoverDetails({ externalEventId, calendarId });

  const validRSVPState =
    responseState === "Proposed" ? ResponseStatusEnum.NotApplicable : responseState;

  const onOpenEventDetails = onOpenCard;

  const submitRescheduleMessage = () => {
    track(TrackingEvents.EVENT_POPOVER.RESCHEDULE_CLICKED);
    const startTimeZoned = startTime.setZone(getRenderTimeZone());
    updateActiveEvent(null);

    const cql = externalEventId ? generateRescheduleCQL(externalEventId) : undefined;

    scrollChatToBottom();
    void processMessage(
      `Reschedule ${title}`,
      {
        eventMentions: [
          {
            externalEventId,
            startTime: startTimeZoned.toISO(),
            title,
          },
        ],
        personMentions: [],
      },
      cql,
    );
    onClose();
  };

  const truncatedTitleForToasts = truncate(title, { length: 20 });

  // If multi cal, owner of event will not match the current user
  const showEventOwner = currentUserId !== calendarId;

  const showFooter = canDelete || canRemove || canRSVP || showEventOwner;

  const selectSchedulingOption = (start: string) => {
    dispatch(
      addSelectedRescheduleOption({
        id: id ?? "",
        externalEventId,
        duration,
        title,
        proposedStartTimeISO: start,
        currentStartTimeISO: startTime.toISO(),
      }),
    );
    onClose();
  };

  const zone = getRenderTimeZone();

  return (
    <PopperWrapper
      id={externalEventId}
      anchorEl={anchorEl}
      side={side}
      onClose={onClose}
      width={250}
    >
      <Header
        title={title}
        description={description}
        attendees={attendees}
        loading={loadingDetails}
        onClick={() => onOpenEventDetails()}
        calendarId={calendarId}
      />
      <div className="cw-px-2">
        {canModify && (
          <QuickReschedule
            shouldExcludeAi={!!shouldExcludeAi}
            submitRescheduleMessage={submitRescheduleMessage}
            onSelectSchedulingOption={selectSchedulingOption}
            externalEventId={externalEventId}
            timeZone={zone}
            duration={duration}
            calendarId={calendarId}
          />
        )}
        {canInviteOthers ? (
          <ListItemButton startIcon={EditOutlined} onClick={onOpenEventDetails}>
            Open & edit event
          </ListItemButton>
        ) : (
          <ListItemButton startIcon={OpenInFull} onClick={onOpenEventDetails}>
            Open event
          </ListItemButton>
        )}

        {canModify && (
          <FlexQuickAction
            externalEventId={externalEventId}
            truncatedTitle={truncatedTitleForToasts}
            calendarId={calendarId}
            loading={loadingDetails}
            onClose={onClose}
            refetch={refetch}
            isFlexible={isFlexible}
            canMarkFlexible={canMarkFlexible}
          />
        )}
        {videoLink?.uri && <JoinVideoLink videoLink={videoLink} onClose={onClose} />}
      </div>
      {showFooter && (
        <Footer
          canDelete={canDelete}
          canRemove={canRemove}
          canRSVP={canRSVP}
          externalEventId={externalEventId}
          calendarId={calendarId}
          responseState={validRSVPState}
          onClose={onClose}
          truncatedTitle={truncatedTitleForToasts}
          showEventOwner={showEventOwner}
          ownerDisplayName={ownerDisplayName}
          loading={loadingDetails}
        />
      )}
    </PopperWrapper>
  );
};
